
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Heart,
  LogOut,
  Menu,
  User,
  X,
  MessageCircle,
  Headphones,
  Building2,
  Globe2,
} from "lucide-react";

import logo from "../assests/images/green-logo.png";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "Hotels", to: "/hotels" },
  { label: "Services", to: "/services" },
  { label: "About Us", to: "/about-us" },
  { label: "Blog", to: "/blogs" },
  { label: "Contact", to: "/contact-us" },
];

export default function Header({
  onLoginClick,
  user,
  setUser,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileUserOpen, setMobileUserOpen] = useState(false);

  const dropdownRef = useRef(null);
  const mobileUserRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        mobileUserRef.current &&
        !mobileUserRef.current.contains(event.target)
      ) {
        setMobileUserOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
  };

  const handleHomeClick = () => {
    closeMobileMenu();

    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBookNow = () => {
    closeMobileMenu();
    navigate("/hotels");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const logout = () => {
    localStorage.removeItem("user");
    setUser?.(null);

    setDropdownOpen(false);
    setMobileUserOpen(false);
    closeMobileMenu();

    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const desktopNavClass = ({ isActive }) => `
    relative inline-flex items-center whitespace-nowrap
    py-2 text-[13px] font-semibold transition-colors duration-200
    after:absolute after:bottom-0 after:left-0
    after:h-[2px] after:rounded-full after:bg-[#168B43]
    after:transition-all after:duration-200
    ${isActive
      ? "text-[#168B43] after:w-full"
      : "text-[#17324D] after:w-0 hover:text-[#168B43] hover:after:w-full"
    }
  `;

  const mobileNavClass = ({ isActive }) => `
    flex w-full items-center justify-between
    rounded-xl px-4 py-3.5 text-sm font-medium
    transition-colors duration-200
    ${isActive
      ? "bg-[#E8F7EE] font-semibold text-[#087D37]"
      : "text-[#17324D] hover:bg-[#F2F8F5]"
    }
  `;


  const Brand = ({ compact = false }) => (
    <div className="flex items-center">
      <img
        src={logo}
        alt="Dubai Budgetive Hotels"
        className={`
        w-auto object-contain
        transition-all duration-300
        ${compact
            ? "h-[48px] max-w-[200px]"
            : "h-[55px] max-w-[250px] lg:h-[75px] lg:max-w-[290px]"
          }
      `}
      />
    </div>
  );


  return (
    <>
      {/* MAIN HEADER */}
      <header
        className={`
          fixed left-0 z-[1000] w-full
          border-b border-[#E6EEE9]
          bg-white text-[#17324D]
          transition-all duration-300
          ${isScrolled
            ? "top-0 shadow-[0_10px_35px_rgba(0,55,35,0.10)]"
            : "top-[38px]"
          }
        `}
      >
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[76px] items-center justify-between gap-4 lg:min-h-[82px]">

            {/* BRAND */}
            <NavLink to="/" onClick={handleHomeClick} className="shrink-0">
              <Brand />
            </NavLink>

            {/* DESKTOP NAVIGATION */}
            <nav
              className="hidden flex-1 items-center justify-center gap-5 lg:flex xl:gap-7 2xl:gap-9"
              aria-label="Main navigation"
            >
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={
                    item.to === "/"
                      ? handleHomeClick
                      : undefined
                  }
                  className={desktopNavClass}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* DESKTOP ACTIONS */}
            <div className="hidden shrink-0 items-center gap-2 lg:flex xl:gap-3">

              <a
                href="tel:+97144473839"
                className="hidden items-center gap-2 rounded-lg px-2 py-2 text-[#17324D] transition hover:text-[#168B43] 2xl:flex"
              >
                <Headphones
                  size={19}
                  className="text-[#168B43]"
                />
                <span className="text-xs font-medium">
                  24/7 Support
                </span>
              </a>

              <NavLink
                to="/wishlist"
                aria-label="Wishlist"
                title="Wishlist"
                className="flex h-10 w-10 items-center justify-center rounded-full text-[#17324D] transition hover:bg-[#E8F7EE] hover:text-[#168B43]"
              >
                <Heart size={19} />
              </NavLink>

              {!user ? (
                <button
                  type="button"
                  onClick={() => onLoginClick?.()}
                  aria-label="Sign in"
                  title="Sign in"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-[#17324D] transition hover:bg-[#E8F7EE] hover:text-[#168B43]"
                >
                  <User size={19} />
                </button>
              ) : (
                <div ref={dropdownRef} className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setDropdownOpen((prev) => !prev)
                    }
                    aria-label="Account menu"
                    aria-expanded={dropdownOpen}
                    className="flex items-center gap-2 rounded-full p-1 transition hover:bg-[#E8F7EE]"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#168B43]/30 bg-[#E8F7EE] text-sm font-bold text-[#168B43]">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </span>

                    <ChevronDown
                      size={15}
                      className={`text-[#64748B] transition-transform ${dropdownOpen ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 top-full mt-3 w-56 overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white py-2 text-[#17324D] shadow-2xl">

                      <div className="border-b border-[#E5EAF0] px-4 py-3">
                        <p className="truncate text-sm font-bold">
                          {user?.name || "My Account"}
                        </p>

                        <p className="mt-0.5 text-xs text-slate-500">
                          Welcome back
                        </p>
                      </div>

                      <NavLink
                        to="/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm transition hover:bg-[#E8F7EE]"
                      >
                        <User size={17} />
                        My Profile
                      </NavLink>

                      <NavLink
                        to="/wishlist"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm transition hover:bg-[#E8F7EE]"
                      >
                        <Heart size={17} />
                        Wishlist
                      </NavLink>

                      <div className="my-1 border-t border-slate-100" />

                      <button
                        type="button"
                        onClick={logout}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
                      >
                        <LogOut size={17} />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* BOOK NOW BUTTON */}
              <button
                type="button"
                onClick={handleBookNow}
                className="ml-1 rounded-full bg-[#168B43] px-5 py-2.5 text-xs font-bold text-white shadow-[0_5px_15px_rgba(22,139,67,0.18)] transition hover:bg-[#087D37]"
              >
                Book Now
              </button>
            </div>

            {/* MOBILE ACTIONS */}
            <div className="flex items-center gap-2 lg:hidden">
              <NavLink
                to="/wishlist"
                aria-label="Wishlist"
                className="flex h-10 w-10 items-center justify-center rounded-xl text-[#17324D]"
              >
                <Heart size={20} />
              </NavLink>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation menu"
                aria-expanded={mobileMenuOpen}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#DCE9E1] text-[#168B43] transition hover:bg-[#E8F7EE]"
              >
                <Menu size={23} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE OVERLAY */}
      <div
        onClick={closeMobileMenu}
        aria-hidden="true"
        className={`
          fixed inset-0 z-[1090] bg-[#032F22]/65
          backdrop-blur-sm transition-opacity duration-300 lg:hidden
          ${mobileMenuOpen
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
          }
        `}
      />

      {/* MOBILE SIDEBAR */}
      <aside
        aria-label="Mobile navigation"
        aria-hidden={!mobileMenuOpen}
        inert={!mobileMenuOpen}
        className={`
          fixed right-0 top-0 z-[1100] h-dvh
          w-[88%] max-w-[390px] overflow-y-auto
          bg-white shadow-2xl transition-transform duration-300
          ease-out lg:hidden
          ${mobileMenuOpen
            ? "translate-x-0"
            : "translate-x-full"
          }
        `}
      >
        {/* SIDEBAR BRAND */}
        <div className="flex items-center justify-between border-b border-[#E6EEE9] bg-white px-5 py-6">
          <NavLink to="/" onClick={handleHomeClick} className="shrink-0">
            <Brand compact />
          </NavLink>

          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label="Close navigation menu"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]"
          >
            <X size={22} />
          </button>
        </div>

        {/* ACCOUNT */}
        <div className="border-b border-slate-100 px-5 py-6">
          {user ? (
            <div ref={mobileUserRef}>
              <button
                type="button"
                onClick={() =>
                  setMobileUserOpen((prev) => !prev)
                }
                aria-expanded={mobileUserOpen}
                className="flex w-full items-center gap-3 text-left"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#E8F7EE] text-base font-bold text-[#168B43]">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-[#17324D]">
                    {user?.name || "My Account"}
                  </span>
                  <span className="mt-1 block text-xs text-slate-500">
                    Manage your account
                  </span>
                </span>

                <ChevronDown
                  size={18}
                  className={`text-slate-500 transition-transform ${mobileUserOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {mobileUserOpen && (
                <div className="mt-4 overflow-hidden rounded-xl bg-[#F2F8F5]">
                  <NavLink
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-[#17324D]"
                  >
                    <User size={17} />
                    My Profile
                  </NavLink>

                  <NavLink
                    to="/wishlist"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-[#17324D]"
                  >
                    <Heart size={17} />
                    Wishlist
                  </NavLink>

                  <button
                    type="button"
                    onClick={logout}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-red-600"
                  >
                    <LogOut size={17} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                closeMobileMenu();
                onLoginClick?.();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#DCE9E1] bg-[#F2F8F5] px-4 py-3.5 text-sm font-semibold text-[#17324D]"
            >
              <User size={18} />
              Sign In / Register
            </button>
          )}
        </div>

        {/* MOBILE NAVIGATION */}
        <nav
          className="space-y-1 px-4 py-5"
          aria-label="Mobile menu"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={
                item.to === "/"
                  ? handleHomeClick
                  : closeMobileMenu
              }
              className={mobileNavClass}
            >
              {item.label}
            </NavLink>
          ))}

          <NavLink
            to="/wishlist"
            onClick={closeMobileMenu}
            className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-[#DCE9E1] px-4 py-3.5 text-sm font-semibold text-[#17324D]"
          >
            <Heart size={18} />
            Wishlist
          </NavLink>

          <button
            type="button"
            onClick={handleBookNow}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#168B43] px-4 py-3.5 text-sm font-bold text-white transition hover:bg-[#087D37]"
          >
            Book Your Stay
          </button>

          <a
            href="https://wa.me/971502477593"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMobileMenu}
            className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-[#168B43] px-4 py-3.5 text-sm font-semibold text-[#168B43]"
          >
            <MessageCircle size={18} />
            Chat With Us
          </a>
        </nav>

        {/* MOBILE CONTACT */}
        <div className="mx-5 mb-6 rounded-2xl bg-[#F2F8F5] p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#17324D]">
            Need Assistance?
          </p>

          <a
            href="tel:+97144473839"
            className="mt-3 flex items-center gap-2 text-sm text-[#17324D]"
          >
            <Headphones
              size={17}
              className="text-[#168B43]"
            />
            +971 4 447 3839
          </a>

          <p className="mt-2 text-xs text-slate-500">
            Our travel support team is here to help.
          </p>
        </div>
      </aside>
    </>
  );
}
